import { useState, useEffect, RefObject, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocumentParams, useRequiredParams } from 'app/router/params';
import { usePermissions } from 'app/api/permission-queries';

import { useParsedSheet, useXlsxFile, useXlsxFiles, useReferenceData, useSelectedData } from 'entities/reference-data';
import { Spinner } from 'app/components';
import { useGetDocument, ReferenceCell, ReferenceDataValue } from 'entities/document';
import { useUser } from 'app/state/contexts/UserContext';
import { formatDate } from 'shared/lib/date';
import { OldModal } from 'app/components';
import { OldModalHandle } from 'shared/overlays/OldModal';
import { MissingSheet, findSuitableNewFile } from 'entities/reference-data';
import { CompareUpdate } from './CompareUpdate';
import { SelectFile } from './SelectFile';
import { SelectSheet } from './SelectSheet';
import { SelectRange } from './SelectRange';
import { showTaggerWarning, tableHasTags } from './updatePermissions';

export const MODAL_STATES = {
  select_file: Symbol('select_file'),
  select_sheet: Symbol('select_sheet'),
  select_range: Symbol('select_range'),
  compare_update: Symbol('show_values'),
};

export type XlsxFile = {
  revisionId: string;
  superId?: string;
  filename: string;
  createdAt: string | number | Date;
  [key: string]: unknown;
};

export type RangeSelection = {
  start: { i: number; j: number };
  end: { i: number; j: number };
  sheet?: string;
  [key: string]: unknown;
};

export type SheetDataResult = {
  data?: ReferenceCell[][];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error?: Error | null;
  isFetching?: boolean;
};

export type SelectedDataResult = {
  data?: ReferenceCell[][];
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  isFetching?: boolean;
};

interface UpdateFinancialDataProps {
  modalRef: RefObject<OldModalHandle>;
  onOpen?: () => void;
  onClose?: () => void;
  onAbort: () => void;
  onSuccess: () => void;
}

export function UpdateFinancialData(props: UpdateFinancialDataProps) {
  const { modalRef, onOpen, onClose, ...innerProps } = props;
  const { documentId, refId } = useRequiredParams('documentId', 'refId');
  const referenceData = useReferenceData(refId, documentId, undefined);
  const files = useXlsxFiles(documentId);

  if (!referenceData.isSuccess || !files.isSuccess) {
    return <Spinner />;
  }
  const filesSorted = (files.data as XlsxFile[]).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  return (
    <OldModal ref={modalRef} onOpen={onOpen} onClose={onClose}>
      <UpdateFinancialDataInner {...innerProps} files={filesSorted} currentReferenceData={referenceData.data as ReferenceDataValue} />
    </OldModal>
  );
}

interface UpdateFinancialDataInnerProps {
  onAbort: () => void;
  onSuccess: () => void;
  currentReferenceData: ReferenceDataValue;
  files: XlsxFile[];
}

function UpdateFinancialDataInner(props: UpdateFinancialDataInnerProps) {
  const { onAbort, onSuccess, currentReferenceData, files } = props;
  const { t } = useTranslation('reference-data');
  const { documentId } = useDocumentParams();
  const { selectedOrganization } = useUser();
  const { documentPermissions } = usePermissions(documentId);
  const taggerWarning = showTaggerWarning(tableHasTags(currentReferenceData.selectionContent), documentPermissions);
  const notice = taggerWarning ? <p className="compare-permission-notice">{t('review-tags.no-tag-permission')}</p> : null;
  const tableIsLinked = !!currentReferenceData.fileRevisionId;
  const currentFile = files.find((f) => f.revisionId === currentReferenceData.fileRevisionId);

  const [selectedFile, setSelectedFile] = useState<XlsxFile>();
  const maybeFile = selectedFile || (findSuitableNewFile(currentReferenceData, currentFile as XlsxFile, files) as XlsxFile | undefined);
  const [modalState, setModalState] = useState(tableIsLinked && maybeFile ? MODAL_STATES.compare_update : MODAL_STATES.select_file);
  const [selectedSheet, setSelectedSheet] = useState<string>();
  const [selection, setSelection] = useState<RangeSelection>();

  const { data: pDocument } = useGetDocument(documentId, selectedOrganization);

  const currentSelection = currentReferenceData.selection as RangeSelection | undefined;
  const maybeSelection = selection || currentSelection;

  const file = useXlsxFile(maybeFile?.revisionId as string);
  const selectedXlsx = file.data?.selectedXlsx;
  const sheetOptions = file.data?.sheetOptions;
  const maybeSheet = selectedSheet || currentSelection?.sheet || sheetOptions?.[0]?.label;
  const sheetData = useParsedSheet(selectedXlsx, maybeSheet, pDocument?.content.locale as string) as SheetDataResult;
  const selectedData = useSelectedData({ ...sheetData, data: sheetData.data ?? [] }, maybeSelection, currentReferenceData) as SelectedDataResult;

  currentReferenceData.selectionContent?.forEach((row) => row.forEach((cell) => (cell.readOnly = true)));
  selectedData.data?.forEach((row) => row.forEach((cell) => (cell.readOnly = true)));

  const handleNewFileClick = () => setModalState(MODAL_STATES.select_file);
  const handleNewSelectionClick = () => setModalState(MODAL_STATES.select_range);
  const handleSaveSelectionClick = () => {
    setModalState(MODAL_STATES.compare_update);
  };
  const handleSelectFile = (file: XlsxFile) => {
    const tableIsLinked = !!currentReferenceData.fileRevisionId;
    if (tableIsLinked) {
      setModalState(MODAL_STATES.compare_update);
    } else {
      setModalState(MODAL_STATES.select_sheet);
    }
    setSelectedFile(file);
  };
  const handleSelectSheet = (sheet: string) => {
    setModalState(MODAL_STATES.select_range);
    setSelectedSheet(sheet);
  };
  const handleChooseSheetClick = () => {
    setModalState(MODAL_STATES.select_sheet);
  };

  const isFetching = [sheetData, selectedData, file].some((l) => (l as { isFetching?: boolean }).isFetching);

  useEffect(() => {
    if (isFetching) {
      return;
    }
    // Cascading fallback (was an intentional switch fallthrough): compare_update runs both
    // checks; select_range runs only the missing-sheet check.
    if (modalState === MODAL_STATES.compare_update || modalState === MODAL_STATES.select_range) {
      if (modalState === MODAL_STATES.compare_update && !selectedData.isSuccess && !selectedData.isLoading) {
        setModalState(MODAL_STATES.select_range);
      }
      const sheetIsMissing = sheetData.isError && sheetData.error instanceof MissingSheet;
      if (sheetIsMissing) {
        setModalState(MODAL_STATES.select_sheet);
      }
    }
  }, [modalState, sheetData, selectedData, isFetching]);

  switch (modalState) {
    case MODAL_STATES.compare_update:
      return (
        <CompareUpdate
          pDocument={pDocument}
          selectedData={selectedData}
          sheetData={sheetData}
          currentReferenceData={currentReferenceData}
          currentFile={currentFile}
          maybeFile={maybeFile}
          maybeSelection={maybeSelection}
          maybeSheet={maybeSheet}
          onNewFileClick={handleNewFileClick}
          onNewSelectionClick={handleNewSelectionClick}
          onAbortClick={onAbort}
          onSaveSuccess={onSuccess}
          taggerWarning={taggerWarning}
          notice={notice}
        />
      );

    case MODAL_STATES.select_file:
      return <SelectFile files={files} currentReferenceData={currentReferenceData} onSelectFile={handleSelectFile} notice={notice} />;

    case MODAL_STATES.select_sheet:
      return (
        <SelectSheet
          file={file}
          maybeFile={maybeFile}
          sheetOptions={sheetOptions}
          currentReferenceData={currentReferenceData}
          onNewFileClick={handleNewFileClick}
          onSelectSheet={handleSelectSheet}
          notice={notice}
        />
      );

    case MODAL_STATES.select_range:
      return (
        <SelectRange
          sheetData={sheetData}
          maybeFile={maybeFile}
          onNewFileClick={handleNewFileClick}
          onChooseSheetClick={handleChooseSheetClick}
          setSelection={setSelection}
          onAbortClick={onAbort}
          onSaveSelectionClick={handleSaveSelectionClick}
          notice={notice}
        />
      );

    default:
      throw new Error('Trying to show invalid modal state');
  }
}

interface CurrentFileBlobProps {
  file?: XlsxFile;
  children?: ReactNode;
}

export function CurrentFileBlob({ file, children }: CurrentFileBlobProps) {
  return (
    <div className="file-info bg-blue-200 align-items-center">
      <div>
        {file && (
          <>
            <span className="fs-3 text-blue-800">
              <i className="fa-regular fa-link"></i> {file.filename}
            </span>
            <br />
            <span className="text-gray-700">{formatDate(file.createdAt)}</span>
          </>
        )}
      </div>
      <div className="d-flex gap-r-1">{children}</div>
    </div>
  );
}
